import {
  ConversationId,
  ConversationThreadId,
  MessageId,
} from "@redotech/effect-api-shared/ids/definitions";
import type { DraftResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/draft";
import FlipBackwardIcon from "@redotech/redo-icons/flip-backward.svg";
import FlipForwardIcon from "@redotech/redo-icons/flip-forward.svg";
import InfoCircleIcon from "@redotech/redo-icons/info-circle.svg";
import { SimpleEmailMessage } from "@redotech/redo-merchant-app-common/simple-email-message";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import type { BgColorValue } from "@redotech/redo-web/theme/box";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";
import { UserImage, UserImageSize } from "@redotech/redo-web/user-image";
import { memo, useState } from "react";
import type { ReplyMode } from "../hooks/use-reply-mode";
import { MessageInput } from "./message-input";

export interface EmailContact {
  name: string | null;
  email: string;
}

export interface FormattedEmailMessage {
  id: MessageId;
  messageId: MessageId;
  threadId: ConversationThreadId;
  senderName: string;
  senderInitials: string;
  isMessageFromCustomer: boolean;
  from: EmailContact;
  to: EmailContact[];
  cc: EmailContact[];
  bcc: EmailContact[];
  recipientSummary: string | null;
  content: string;
  htmlContent: string | null;
  timestamp: Date;
  formattedTime: string;
  preview: string;
}

function headerBgColor(isMessageFromCustomer: boolean): BgColorValue {
  return isMessageFromCustomer ? "secondary" : "quaternary";
}

export const EmailMessageCard = memo(function EmailMessageCard({
  message,
  conversationId,
  initialReplyMode = "reply",
  initialDraft,
  isExpanded,
  isLast,
  isReplyOpen,
  onToggleExpand,
  onReply,
  onReplyAll,
  onForward,
  onDiscardDraft,
}: {
  message: FormattedEmailMessage;
  conversationId: ConversationId;
  initialReplyMode?: ReplyMode;
  initialDraft?: DraftResponse | null;
  isExpanded: boolean;
  isLast?: boolean;
  isReplyOpen?: boolean;
  onToggleExpand: () => void;
  onReply?: () => void;
  onReplyAll?: () => void;
  onForward?: () => void;
  onDiscardDraft?: () => void;
}) {
  const showReply = isExpanded && isReplyOpen;
  const isLastWithoutReply = isLast && !showReply;
  return (
    <Flex
      borderBottomWidth={isLastWithoutReply ? "1px" : undefined}
      borderColor="primary"
      borderLeftWidth="1px"
      borderRightWidth="1px"
      borderStyle="solid"
      bottomLeftRadius={isLastWithoutReply ? "sm" : undefined}
      bottomRightRadius={isLastWithoutReply ? "sm" : undefined}
      dir="column"
      gap="none"
    >
      <MessageHeader
        cc={message.cc}
        formattedTime={message.formattedTime}
        from={message.from}
        isExpanded={isExpanded}
        isMessageFromCustomer={message.isMessageFromCustomer}
        onToggleExpand={onToggleExpand}
        preview={message.preview}
        senderName={message.senderName}
        to={message.to}
      />
      {isExpanded && (
        <Flex bgColor="primary" dir="column" gap="md" p="xl">
          <MessageContent
            content={message.content}
            htmlContent={message.htmlContent}
          />
          {!showReply && (
            <MessageActions
              onForward={onForward}
              onReply={onReply}
              onReplyAll={onReplyAll}
            />
          )}
        </Flex>
      )}
      {showReply && (
        <Flex
          bgColor="primary"
          borderBottomWidth={isLast ? "1px" : undefined}
          borderColor="primary"
          borderStyle="solid"
          bottomLeftRadius={isLast ? "sm" : undefined}
          bottomRightRadius={isLast ? "sm" : undefined}
          dir="column"
          p="xl"
          pt="none"
        >
          <MessageInput
            conversationId={conversationId}
            initialDraft={initialDraft}
            initialReplyMode={initialReplyMode}
            inReplyToMessageId={message.messageId}
            onDiscardDraft={onDiscardDraft}
            threadId={message.threadId}
          />
        </Flex>
      )}
    </Flex>
  );
});

const MessageHeader = memo(function MessageHeader({
  senderName,
  isMessageFromCustomer,
  from,
  to,
  cc,
  formattedTime,
  preview,
  isExpanded,
  onToggleExpand,
}: {
  senderName: string;
  isMessageFromCustomer: boolean;
  from: EmailContact;
  to: EmailContact[];
  cc: EmailContact[];
  formattedTime: string;
  preview: string;
  isExpanded: boolean;
  onToggleExpand: () => void;
}) {
  const [mouseDownLoc, setMouseDownLoc] = useState<
    { x: number; y: number } | undefined
  >();

  return (
    <Flex
      align="center"
      bgColor={headerBgColor(isMessageFromCustomer)}
      cursor="pointer"
      gap="md"
      justify="space-between"
      onMouseDown={(e) => {
        setMouseDownLoc({ x: e.clientX, y: e.clientY });
      }}
      onMouseUp={(e) => {
        if (
          mouseDownLoc &&
          Math.abs(e.clientX - mouseDownLoc.x) < 5 &&
          Math.abs(e.clientY - mouseDownLoc.y) < 5
        ) {
          onToggleExpand();
        }
      }}
      p="xl"
    >
      <Flex
        align="flex-start"
        borderColor="primary"
        borderStyle="solid"
        gap="md"
        grow={1}
        style={{ minWidth: 0 }}
      >
        <SenderAvatar senderName={senderName} />
        <Flex dir="column" gap="none" grow={1} style={{ minWidth: 0 }}>
          <Text fontSize="sm" fontWeight="semibold" textColor="primary">
            {senderName}
          </Text>
          {!isExpanded && (
            <Text
              fontSize="xs"
              textColor="secondary"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
            >
              {preview}
            </Text>
          )}
          {isExpanded && (
            <Flex align="center" gap="xs">
              <Tooltip
                placement="bottom-start"
                title={
                  <EmailAddressDetails
                    cc={cc}
                    formattedTime={formattedTime}
                    from={from}
                    to={to}
                  />
                }
              >
                <Flex
                  align="center"
                  onMouseUp={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  shrink={0}
                  style={{
                    color: "var(--redo-colors-foreground-fg-disabled)",
                    height: 16,
                    width: 16,
                  }}
                >
                  <InfoCircleIcon />
                </Flex>
              </Tooltip>
              <Text fontSize="xs" textColor="tertiary">
                {formatRecipientLine(to, cc)}
              </Text>
            </Flex>
          )}
        </Flex>
      </Flex>
      <Flex align="center" gap="xs" justify="center" shrink={0}>
        <Text fontSize="xs" textColor="tertiary">
          {formattedTime}
        </Text>
      </Flex>
    </Flex>
  );
});

const SenderAvatar = memo(function SenderAvatar({
  senderName,
}: {
  senderName: string;
}) {
  return (
    <UserImage
      alt={`${senderName} avatar`}
      imageUrl={null}
      name={senderName}
      size={UserImageSize.SMALL}
    />
  );
});

function formatContactDisplay(contact: EmailContact): string {
  if (contact.name) {
    return `${contact.name} <${contact.email}>`;
  }
  return contact.email;
}

function formatRecipientLine(to: EmailContact[], cc?: EmailContact[]): string {
  const parts: string[] = [];
  if (to.length > 0) {
    parts.push(`to: ${to.map((a) => a.email).join(", ")}`);
  }
  if (cc && cc?.length > 0) {
    parts.push(`cc: ${cc.map((a) => a.email).join(", ")}`);
  }
  return parts.join(", ");
}

function EmailAddressDetails({
  from,
  to,
  cc,
  formattedTime,
}: {
  from: EmailContact;
  to: EmailContact[];
  cc: EmailContact[];
  formattedTime: string;
}) {
  return (
    <Flex dir="column" gap="md">
      <TooltipRow label="From" value={formatContactDisplay(from)} />
      {to.length > 0 && (
        <TooltipRow
          label="To"
          value={to.map(formatContactDisplay).join(", ")}
        />
      )}
      {cc.length > 0 && (
        <TooltipRow
          label="CC"
          value={cc.map(formatContactDisplay).join(", ")}
        />
      )}
      <TooltipRow label="Date" value={formattedTime} />
    </Flex>
  );
}

function TooltipRow({ label, value }: { label: string; value: string }) {
  return (
    <Flex dir="column" gap="none">
      <Text
        fontSize="xs"
        style={{ color: "var(--redo-colors-text-text-quarterary-500)" }}
      >
        {label}
      </Text>
      <Text fontSize="xs">{value}</Text>
    </Flex>
  );
}

const MessageContent = memo(function MessageContent({
  content,
  htmlContent,
}: {
  content: string;
  htmlContent: string | null;
}) {
  return (
    <Flex dir="column" ml="5xl">
      <SimpleEmailMessage
        messageContent={
          htmlContent
            ? { type: "messageHtml", messageHtml: htmlContent }
            : { type: "messageText", messageText: content || "(No content)" }
        }
      />
    </Flex>
  );
});

const MessageActions = memo(function MessageActions({
  onReply,
  onReplyAll,
  onForward,
}: {
  onReply?: () => void;
  onReplyAll?: () => void;
  onForward?: () => void;
}) {
  return (
    <Flex gap="md" ml="5xl" pt="md">
      <RedoButton
        hierarchy="secondary"
        IconLeading={FlipBackwardIcon}
        onClick={onReply}
        size="md"
        text="Reply"
      />
      <RedoButton
        hierarchy="secondary"
        IconLeading={FlipBackwardIcon}
        onClick={onReplyAll}
        size="md"
        text="Reply all"
      />
      <RedoButton
        hierarchy="secondary"
        IconLeading={FlipForwardIcon}
        onClick={onForward}
        size="md"
        text="Forward"
      />
    </Flex>
  );
});
